import { Directive, ElementRef, inject } from '@angular/core';

/**
 * Marks an `<a>` that acts as a button rather than a link.
 *
 * Such anchors used to carry `href="#"` so they would look and behave like
 * links. While AngularJS was still around its `$location` link rewriting
 * swallowed the resulting navigation; without it the `#` navigation happens for
 * real, which surfaces as unexpected state changes and `beforeunload` prompts.
 *
 * Dropping the `href` stops the navigation but also drops the pointer cursor,
 * the implicit focusability and the keyboard activation that came with it, so
 * this directive restores all three.
 */
@Directive({
  selector: 'a[actionLink]',
  standalone: true,
  host: {
    class: 'tw-cursor-pointer',
    role: 'button',
    tabindex: '0',
    '(keydown.enter)': 'activate($event)',
    '(keydown.space)': 'activate($event)',
  },
})
export class ActionLinkDirective {
  private readonly elementRef =
    inject<ElementRef<HTMLAnchorElement>>(ElementRef);

  /** Mirrors native button behavior: Enter and Space both click the anchor. */
  activate(event: Event) {
    event.preventDefault();
    this.elementRef.nativeElement.click();
  }
}
